/**
 * The canvas takes what is left of the window — `property-maps-v2.md` §7.
 *
 * The editor is a flex column inside the record page, which is a flex column
 * inside a scroll container that is **not** one: `flex-1` therefore grows the
 * editor to its own content and no further, so the canvas fell back to its
 * 560px floor and an iPad in portrait drew a map 560px tall with 250px of
 * page background under it.
 *
 * Rather than make every record page exactly a viewport tall — which would
 * move the scroll from the frame into the page on contacts, jobs and invoices
 * alike — the one element that wants the room asks for it: whatever is above
 * it is measured, and the rest of the window is its minimum height.
 *
 * It re-measures on `resize` and `orientationchange`, which is §7's rotation
 * rule, and on nothing else: the chrome above the canvas (the header, the tab
 * strip, the map bar, the view-only and measuring banners) changes height
 * only when the window does or when a banner appears, and a banner appearing
 * re-renders the editor anyway.
 */
import { useCallback, useEffect, useLayoutEffect, useState, type RefObject } from 'react'

/** Never smaller than this, however short the window is (board 2). */
export const CANVAS_FLOOR = 560

/**
 * What is left under the card, so its bottom edge is on the page.
 *
 * The editor is a card now, like every other tab body, and a card whose
 * rounded bottom corners are a pixel past the fold is a card with no bottom.
 * 20 is the record page's own bottom gutter; the extra 1 is the card's
 * border, which `getBoundingClientRect` puts above the canvas row but not
 * below it.
 */
export const CARD_GUTTER = 21

export function useFillHeight(
  box: RefObject<HTMLElement | null>,
  enabled: boolean,
  /** Subtracted from what the window has left — `CARD_GUTTER` in a card. */
  gutter = 0,
): number {
  const [height, setHeight] = useState(CANVAS_FLOOR)

  const measure = useCallback(() => {
    const element = box.current
    if (!enabled || !element) {
      setHeight(CANVAS_FLOOR)
      return
    }
    /* `dvh` by hand: `innerHeight` already excludes the browser chrome a
       phone shows and hides, which is the whole reason §6.2 uses `dvh`. */
    const { top } = element.getBoundingClientRect()
    setHeight(Math.max(CANVAS_FLOOR, Math.round(window.innerHeight - top - gutter)))
  }, [box, enabled, gutter])

  /*
   * Every render, because the chrome above the canvas gains and loses rows:
   * the view-only banner, the measuring strip, a map name that wraps. It is
   * one `getBoundingClientRect` and `setHeight` bails on an unchanged number,
   * so a render that moves nothing costs a read and stops.
   */
  useLayoutEffect(measure)

  /* The listeners, once: §7's rotation rule and an ordinary window resize. */
  useEffect(() => {
    window.addEventListener('resize', measure)
    window.addEventListener('orientationchange', measure)
    return () => {
      window.removeEventListener('resize', measure)
      window.removeEventListener('orientationchange', measure)
    }
  }, [measure])

  return height
}
